import type { MeetingDetail } from '@fos/shared/contracts/meetings';
import { RefreshCw, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { errorMessage } from '../../../lib/api';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { AIBlock } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Section } from '../../../ui/Section';
import { Spinner } from '../../../ui/Spinner';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { meetingDurationText } from '../meeting-format';
import { DecisionsBlock } from './DecisionsBlock';
import { ProposalsBlock } from './ProposalsBlock';

/**
 * Wynik AI w widoku spotkania (Figma 05): podsumowanie z podpisem „AI z transkrypcji”, ustalenia, pytania, blok
 * propozycji zadań. Stan AI (DESIGN §1.5): nieprzetworzone (zlecenie), AI pracuje (odświeżanie), błąd z ponowieniem,
 * wynik nieaktualny po zmianie transkrypcji. Awaria AI nie blokuje notatek ani ręcznych zadań.
 */

export function useProcessMeeting(meeting: Pick<MeetingDetail, 'id' | 'version'>) {
  const utils = trpc.useUtils();
  const process = trpc.meetings.process.useMutation({
    onSuccess: async () => {
      await utils.meetings.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err, 'Nie udało się zlecić przetworzenia.')),
  });
  return {
    run: () => process.mutate({ id: meeting.id, version: meeting.version }),
    pending: process.isPending,
  };
}

function SectionTitle({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2 id={id} className="text-body-s font-semibold text-fg">
      {children}
    </h2>
  );
}

export function MeetingAiSection({ meeting }: { meeting: MeetingDetail }) {
  const access = useAccess();
  const { run, pending } = useProcessMeeting(meeting);
  const { ai } = meeting;
  const hasResult = Boolean(meeting.summaryMd || meeting.decisions.length || meeting.questions.length);
  const hasProposals = meeting.proposals.length > 0;
  const pendingProposals = meeting.proposals.some((p) => p.status === 'proposed');
  const duration = meetingDurationText(meeting);
  const source = meeting.hasTranscript ? 'AI z transkrypcji' : 'AI z notatek';

  const status = (() => {
    if (ai.status === 'queued') {
      return (
        <AIBlock
          tone="subtle"
          title={
            <span className="inline-flex items-center gap-2">
              AI pracuje
              <Spinner size={14} className="text-fg-muted" />
            </span>
          }
          actions={
            ai.stuck && meeting.canProcess ? (
              <Button
                variant="secondary"
                size="s"
                icon={RefreshCw}
                onClick={run}
                loading={pending}
                className={TOUCH_BUTTON}
              >
                Zleć ponownie
              </Button>
            ) : undefined
          }
        >
          <p role="status">
            {ai.stuck
              ? 'Trwa to dłużej niż zwykle. Możesz zlecić przetworzenie ponownie.'
              : 'Podsumowanie, ustalenia i propozycje zadań pojawią się tutaj. Możesz w tym czasie pracować dalej.'}
          </p>
        </AIBlock>
      );
    }
    if (ai.status === 'failed') {
      return (
        <AIBlock
          tone="subtle"
          title="AI nie przetworzyło spotkania"
          actions={
            meeting.canProcess ? (
              <Button
                variant={pendingProposals ? 'secondary' : 'primary'}
                size="s"
                icon={RefreshCw}
                onClick={run}
                loading={pending}
                className={TOUCH_BUTTON}
              >
                Spróbuj ponownie
              </Button>
            ) : undefined
          }
          footnote="Notatki i ręczne zadania działają bez AI."
        >
          <p role="alert" className="text-fg">
            {ai.error ?? 'Nie udało się przetworzyć spotkania.'}
          </p>
        </AIBlock>
      );
    }
    if (ai.status === 'none') {
      if (!meeting.canProcess) return null;
      return (
        <AIBlock
          tone="subtle"
          title="AI przygotuje notatkę ze spotkania"
          actions={
            <Button variant="primary" size="s" icon={Sparkles} onClick={run} loading={pending} className={TOUCH_BUTTON}>
              {meeting.hasTranscript ? 'Przetwórz transkrypcję' : 'Przetwórz notatki'}
            </Button>
          }
          footnote="Ty decydujesz, które zadania trafią na listę, a które ustalenia do Wiedzy."
        >
          Podsumowanie, ustalenia, otwarte pytania i propozycje zadań.
        </AIBlock>
      );
    }
    if (ai.stale && meeting.canProcess) {
      return (
        <AIBlock
          tone="subtle"
          title="Transkrypcja zmieniła się po przetworzeniu"
          actions={
            <Button
              variant="secondary"
              size="s"
              icon={RefreshCw}
              onClick={run}
              loading={pending}
              className={TOUCH_BUTTON}
            >
              Przetwórz ponownie
            </Button>
          }
        >
          Wynik poniżej dotyczy poprzedniej wersji. Dodane zadania nie zostaną zdublowane.
        </AIBlock>
      );
    }
    return null;
  })();

  return (
    // wynik AI (podsumowanie, ustalenia, pytania, propozycje) w jednej białej sekcji (DECISIONS 2026-10-07)
    <Section className="flex flex-col gap-8">
      {status}

      {meeting.summaryMd && (
        <section aria-labelledby="podsumowanie">
          <SectionTitle id="podsumowanie">Podsumowanie</SectionTitle>
          <p className="mt-2 text-body-s break-words whitespace-pre-line text-fg-secondary">{meeting.summaryMd}</p>
          <p className="mt-2 flex flex-wrap items-center gap-1.5 text-caption text-fg-muted">
            <Sparkles size={13} strokeWidth={1.75} aria-hidden className="text-accent" />
            <span>{[source, duration].filter(Boolean).join(' · ')}</span>
            {ai.simulated && <span>· symulacja (wynik z reguł, bez dostawcy AI)</span>}
          </p>
        </section>
      )}

      <DecisionsBlock meeting={meeting} />

      {meeting.questions.length > 0 && (
        <section aria-labelledby="pytania">
          <SectionTitle id="pytania">Otwarte pytania</SectionTitle>
          <ul className="mt-3 flex flex-col gap-2.5">
            {meeting.questions.map((q) => (
              <li key={q} className="flex gap-2.5 text-body-s text-fg">
                <span aria-hidden className="mt-[0.65em] size-1.5 shrink-0 rounded-full bg-line-strong" />
                <span className="min-w-0 break-words">{q}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {hasProposals && access.can('tasks') && <ProposalsBlock meeting={meeting} />}

      {ai.status === 'done' && !hasResult && !hasProposals && (
        <p className="text-body-s text-fg-muted">AI nie znalazło w rozmowie ustaleń ani zadań.</p>
      )}
    </Section>
  );
}
